/**
 * Text folding and tokenisation (engine.md §2). The ONE definition of a word, shared by the FTS index,
 * the reference matcher, the View compiler and search.
 */

const MARKS = /\p{M}/gu;
const WHITESPACE = /\s+/gu;
const TOKEN = /[\p{L}\p{N}]+/gu;

/** NFKD, strip combining marks, lower-case, collapse whitespace, trim. 'Ёлка' -> 'елка'. */
export function foldText(s: string): string {
  return s.normalize('NFKD').replace(MARKS, '').toLowerCase().replace(/ς/g, 'σ').replace(WHITESPACE, ' ').trim();
}

/** Folded word tokens: runs of letters and digits. */
export function tokens(s: string): string[] {
  return foldText(s).match(TOKEN) ?? [];
}

/** True when `needle` occurs in `haystack` as a consecutive run (token-phrase match). */
export function containsPhrase(haystack: readonly string[], needle: readonly string[]): boolean {
  if (needle.length === 0) return false;
  const last = haystack.length - needle.length;
  outer: for (let i = 0; i <= last; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return true;
  }
  return false;
}

const LETTER = /\p{L}/u;
const CYRILLIC = /\p{Script=Cyrillic}/u;
const LATIN = /\p{Script=Latin}/u;

/**
 * The writing system of a text (models.md §8.2): count its letters; 'cyrillic' when at least half are
 * Cyrillic, 'latin' when at least half are Latin, else 'other' (also for a text without letters).
 * The `script` condition tests detectScript(title + ' ' + excerpt).
 */
export function detectScript(text: string): 'latin' | 'cyrillic' | 'other' {
  let letters = 0;
  let cyrillic = 0;
  let latin = 0;
  for (const ch of text) {
    if (!LETTER.test(ch)) continue;
    letters++;
    if (CYRILLIC.test(ch)) cyrillic++;
    else if (LATIN.test(ch)) latin++;
  }
  if (letters === 0) return 'other';
  if (cyrillic * 2 >= letters) return 'cyrillic';
  if (latin * 2 >= letters) return 'latin';
  return 'other';
}
